/**
 * Admin API Service
 *
 * Centralized API client for admin endpoints with automatic:
 * - Auth header injection
 * - 401 handling (redirects to login)
 * - Error normalization
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Store logout callback - will be set by AdminAuthProvider
let logoutCallback = null;

// Track when last successful login occurred to prevent race conditions
// where stale 401 responses trigger logout after a fresh login
let lastLoginTimestamp = 0;
const LOGIN_GRACE_PERIOD_MS = 3000; // Ignore 401s for 3 seconds after login

/**
 * Register a logout callback to be called on 401 errors
 * This is called by AdminAuthProvider on mount
 */
export const setLogoutCallback = (callback) => {
  logoutCallback = callback;
};

/**
 * Mark that a successful login just occurred
 * Call this right after storing the new auth token
 */
export const markLoginSuccess = () => {
  lastLoginTimestamp = Date.now();
};

/**
 * Check if we're within the grace period after a fresh login
 */
const isWithinLoginGracePeriod = () => {
  return Date.now() - lastLoginTimestamp < LOGIN_GRACE_PERIOD_MS;
};

/**
 * Get auth headers for admin API calls
 * Exported so components can use it directly if needed
 */
export const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('googleAuthToken')}`,
  'Content-Type': 'application/json'
});

/**
 * Handle 401 responses by triggering logout
 * @param {string} reason - The reason for logout ('inactivity' or 'expired')
 */
const handle401 = (reason = 'expired') => {
  // Ignore 401s that arrive shortly after a successful login
  // This prevents race conditions where stale requests trigger logout
  if (isWithinLoginGracePeriod()) {
    console.warn('Admin API: Ignoring 401 during login grace period (likely stale request)');
    return;
  }

  console.warn(`Admin API: 401 Unauthorized - ${reason}`);
  localStorage.removeItem('googleAuthToken');
  localStorage.removeItem('adminUser');

  if (logoutCallback) {
    logoutCallback(reason);
  } else {
    // Fallback if callback not registered - reload to trigger auth check
    window.location.reload();
  }
};

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (e.g., '/api/admin/students')
 * @param {object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>} - Fetch response
 */
const request = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;

  const config = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  };

  const response = await fetch(url, config);

  // Handle 401 - session expired
  if (response.status === 401) {
    // Try to get the error detail to determine reason
    let reason = 'expired';
    try {
      const data = await response.clone().json();
      if (data.detail && data.detail.toLowerCase().includes('inactivity')) {
        reason = 'inactivity';
      }
    } catch {
      // Couldn't parse response, use default reason
    }

    handle401(reason);
    // Throw to prevent further processing
    const message = reason === 'inactivity'
      ? 'Session expired due to inactivity. Please log in again.'
      : 'Session expired. Please log in again.';
    throw new Error(message);
  }

  return response;
};

/**
 * GET request
 */
export const get = async (endpoint) => {
  return request(endpoint, { method: 'GET' });
};

/**
 * POST request
 */
export const post = async (endpoint, data) => {
  return request(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * PUT request
 */
export const put = async (endpoint, data) => {
  return request(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * DELETE request
 */
export const del = async (endpoint) => {
  return request(endpoint, { method: 'DELETE' });
};

/**
 * POST with FormData (for file uploads)
 * Note: Don't set Content-Type header - browser will set it with boundary
 */
export const postFormData = async (endpoint, formData) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('googleAuthToken')}`,
      // Don't set Content-Type - let browser set it for FormData
    },
    body: formData,
  });

  if (response.status === 401) {
    handle401();
    throw new Error('Session expired. Please log in again.');
  }

  return response;
};

/**
 * Helper to get JSON from response with error handling
 */
export const getJson = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return response.json();
};

/**
 * Drop-in replacement for fetch() that handles 401 errors
 * Components can replace: fetch(url, options) with adminFetch(url, options)
 *
 * @param {string} url - Full URL or relative endpoint
 * @param {object} options - Standard fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export const adminFetch = async (url, options = {}) => {
  const response = await fetch(url, options);

  // Handle 401 - session expired
  if (response.status === 401) {
    handle401();
    // Return the response so component can handle the error message
    // but the logout will already be triggered
  }

  return response;
};

// Default export for convenience
const adminApi = {
  get,
  post,
  put,
  delete: del,
  postFormData,
  getJson,
  setLogoutCallback,
  getAuthHeaders,
  markLoginSuccess,
  fetch: adminFetch,
};

export default adminApi;
